import { createOperationInvocationId } from '../operations/invocation';

export type TemplateSaveSource = 'ui' | 'webmcp';

export type TemplateSaveSettlement =
  | { status: 'saved' }
  | { status: 'cancelled' }
  | { status: 'blocked'; message: string }
  | { status: 'stale' }
  | { status: 'failed'; message: string }
  | { status: 'awaiting_confirmation' };

export type TemplateSaveMetadataForUpsert = {
  saveInvocationId: string;
  saveSource: TemplateSaveSource;
  expectedWorkingRevision: number;
};

type SaveDialogResult = 'dialog' | 'blocked';

type PendingSave = {
  expectedWorkingRevision: number;
  saveInvocationId: string;
  saveSource: TemplateSaveSource;
  resolve: (result: TemplateSaveSettlement) => void;
};

let openSave: (() => SaveDialogResult) | null = null;
let closeSave: (() => void) | null = null;
let confirmSave: (() => void) | null = null;
let pending: PendingSave | null = null;
let armedRevision: number | null = null;
let armedSaveInvocationId: string | null = null;
let armedSaveSource: TemplateSaveSource = 'ui';

export function registerTemplateSaveRequest(
  open: () => SaveDialogResult,
  close: () => void,
  confirm: () => void,
): () => void {
  openSave = open;
  closeSave = close;
  confirmSave = confirm;
  return () => {
    if (openSave === open) {
      openSave = null;
      closeSave = null;
      confirmSave = null;
      armedRevision = null;
      armedSaveInvocationId = null;
      armedSaveSource = 'ui';
      settleTemplateSave({ status: 'cancelled' });
    }
  };
}

export function armTemplateSaveInvocation(request: {
  expectedWorkingRevision: number;
  saveInvocationId?: string;
  saveSource?: TemplateSaveSource;
}): void {
  armedRevision = request.expectedWorkingRevision;
  armedSaveInvocationId =
    request.saveInvocationId ?? armedSaveInvocationId ?? createOperationInvocationId();
  armedSaveSource = request.saveSource ?? armedSaveSource;
}

export function getPendingTemplateSave(): TemplateSaveMetadataForUpsert | null {
  if (pending) {
    return {
      expectedWorkingRevision: pending.expectedWorkingRevision,
      saveInvocationId: pending.saveInvocationId,
      saveSource: pending.saveSource,
    };
  }
  if (armedRevision != null && armedSaveInvocationId) {
    return {
      expectedWorkingRevision: armedRevision,
      saveInvocationId: armedSaveInvocationId,
      saveSource: armedSaveSource,
    };
  }
  return null;
}

export function getTemplateSaveMetadataForUpsert(): TemplateSaveMetadataForUpsert | null {
  return getPendingTemplateSave();
}

export function settleTemplateSave(result: TemplateSaveSettlement): void {
  const current = pending;
  pending = null;
  armedRevision = null;
  armedSaveInvocationId = null;
  armedSaveSource = 'ui';
  current?.resolve(result);
}

export function requestTemplateSave(request: {
  expectedWorkingRevision: number;
  saveInvocationId?: string;
  saveSource?: TemplateSaveSource;
  userConfirmed?: boolean;
  cancel?: boolean;
  signal?: AbortSignal;
}): Promise<TemplateSaveSettlement> {
  if (!openSave || !confirmSave) {
    return Promise.reject(new Error('Template Builder save is not available.'));
  }
  if (request.signal?.aborted) {
    return Promise.resolve({ status: 'cancelled' });
  }
  if (request.cancel) {
    closeSave?.();
    if (pending) {
      settleTemplateSave({ status: 'cancelled' });
    } else {
      armedRevision = null;
      armedSaveInvocationId = null;
      armedSaveSource = 'ui';
    }
    return Promise.resolve({ status: 'cancelled' });
  }
  if (pending) {
    return Promise.reject(new Error('A save confirmation is already open.'));
  }

  if (!request.userConfirmed) {
    let outcome: SaveDialogResult;
    try {
      outcome = openSave();
    } catch (error) {
      return Promise.reject(error);
    }
    if (outcome === 'blocked') {
      return waitForSettlement(request, request.signal);
    }
    armTemplateSaveInvocation({
      expectedWorkingRevision: request.expectedWorkingRevision,
      saveInvocationId: request.saveInvocationId,
      saveSource: request.saveSource,
    });
    return Promise.resolve({ status: 'awaiting_confirmation' });
  }

  return new Promise((resolve, reject) => {
    const finish = (result: TemplateSaveSettlement) => {
      request.signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onAbort = () => {
      closeSave?.();
      settleTemplateSave({ status: 'cancelled' });
    };
    const saveInvocationId =
      request.saveInvocationId ?? armedSaveInvocationId ?? createOperationInvocationId();
    const saveSource = request.saveSource ?? armedSaveSource;
    pending = {
      expectedWorkingRevision: request.expectedWorkingRevision,
      saveInvocationId,
      saveSource,
      resolve: finish,
    };
    request.signal?.addEventListener('abort', onAbort, { once: true });
    try {
      const outcome = openSave?.();
      if (outcome === 'dialog') {
        confirmSave?.();
      }
    } catch (error) {
      pending = null;
      armedRevision = null;
      armedSaveInvocationId = null;
      armedSaveSource = 'ui';
      request.signal?.removeEventListener('abort', onAbort);
      reject(error);
    }
  });
}

function waitForSettlement(
  request: {
    expectedWorkingRevision: number;
    saveInvocationId?: string;
    saveSource?: TemplateSaveSource;
  },
  signal?: AbortSignal,
): Promise<TemplateSaveSettlement> {
  return new Promise((resolve) => {
    const finish = (result: TemplateSaveSettlement) => {
      signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onAbort = () => {
      closeSave?.();
      settleTemplateSave({ status: 'cancelled' });
    };
    const saveInvocationId =
      request.saveInvocationId ?? armedSaveInvocationId ?? createOperationInvocationId();
    pending = {
      expectedWorkingRevision: request.expectedWorkingRevision,
      saveInvocationId,
      saveSource: request.saveSource ?? armedSaveSource,
      resolve: finish,
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
